<template>
  <div v-if="shouldShowPopup" class="ags-popup" :class="{ 'mobile': isMobile }">
    <!-- Header Section -->
    <div class="popup-header">
      <h3 class="unit-name">{{ displayName }}</h3>
    </div>
    
    <!-- Content Section -->
    <div class="popup-content">
      <!-- Geographic Information -->
      <div class="geographic-section">
        <h4>Geographic Information</h4>
        <div class="geographic-info">
          <div v-if="stateCode" class="geographic-item">
            <span class="label">State:</span>
            <span class="value">{{ getStateName(stateCode) }} ({{ stateCode }})</span>
          </div>
          <div v-if="countyCode" class="geographic-item">
            <span class="label">County:</span>
            <span class="value">{{ getCountyName(countyCode) || `County ${countyCode}` }}</span>
          </div>
          <div v-if="tractCode" class="geographic-item">
            <span class="label">Census Tract:</span>
            <span class="value code-value">{{ formatTractCode(tractCode) }}</span>
          </div>
          <div v-if="blockGroupCode && unitType === 'block'" class="geographic-item">
            <span class="label">Block Group:</span>
            <span class="value code-value">{{ formatBlockGroupCode(blockGroupCode) }}</span>
          </div>
        </div>
      </div>

      <!-- AGS Intensity Level Section -->
      <div v-if="agsIntensityLevel && agsIntensityLevel !== 'no_data'" class="ags-intensity-section">
        <div class="section-header-with-info">
          <h4>AGS Data Analysis</h4>
          <div class="info-tooltip-container">
            <svg class="info-icon-small" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="info-tooltip">
              <strong>AGS Data Intensity</strong>
              <p>Relative intensity of selected AGS variables compared to similar geographic areas.</p>
            </div>
          </div>
        </div>
        
        <!-- AGS Intensity Summary -->
        <div class="intensity-summary">
          <div class="intensity-bar-container">
            <div class="label-with-tooltip">
              <label class="intensity-label">Data Intensity:</label>
              <div class="info-tooltip-container">
                <svg class="info-icon-tiny" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div class="info-tooltip">
                  <strong>AGS Data Intensity</strong>
                  <p>Shows the relative intensity of AGS data values for this area compared to similar geographic areas.</p>
                </div>
              </div>
            </div>
            <div class="intensity-bar" :aria-label="`AGS intensity: ${displayedIntensityPercent}%`">
              <div 
                class="intensity-fill" 
                :style="{ 
                  width: displayedIntensityPercent + '%',
                  backgroundColor: getAgsIntensityColor(effectiveAgsLevel)
                }"
              ></div>
            </div>
            <span class="intensity-text">{{ displayedIntensityPercent }}%</span>
          </div>
          <div class="combined-value">
            <span class="label">Classification:</span>
            <span 
              class="value ags-badge"
              :style="{ backgroundColor: getAgsIntensityColor(effectiveAgsLevel), color: 'white' }"
            >
              {{ formatAgsIntensityLevel(effectiveAgsLevel) }}
            </span>
          </div>
          
          <!-- Ranking Row -->
          <div v-if="hasRankingData" class="combined-value">
            <span class="label">
              Ranking:
              <div class="info-tooltip-container">
                <svg class="info-icon-tiny" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div class="info-tooltip">
                  <strong>Area Ranking</strong>
                  <p>Shows where this area ranks based on AGS intensity compared to similar areas.</p>
                  <p>Rankings update when the map viewport changes.</p>
                </div>
              </div>
            </span>
            <span class="value">
              {{ formatRanking(agsRanking) }}
              <span class="percentile-badge" :class="getPercentileBadgeClass(agsRanking.percentile)">
                {{ getPercentileDescription(agsRanking.percentile) }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <!-- AGS Data Section -->
      <div v-if="hasAgsData" class="ags-data-section">
        <div class="section-header-with-info">
          <h4>{{ selectedCategoryDisplayName || 'AGS Data' }} ({{ dataYear }})</h4>
          <div class="info-tooltip-container">
            <svg class="info-icon-small" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="info-tooltip">
              <strong>AGS Data Values</strong>
              <p>American Geographic Survey data for selected variables in this geographic area.</p>
            </div>
          </div>
        </div>
        
        <div class="ags-table-container">
          <table class="ags-table" role="table" :aria-label="`AGS data for ${displayName}`">
            <thead>
              <tr>
                <th scope="col">Variable</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="variable in displayedVariables" 
                :key="variable.path"
              >
                <td class="variable-type">
                  <span class="variable-label">{{ variable.label }}</span>
                </td>
                <td class="variable-value">
                  <span class="value-number">{{ formatAgsVariableValue(variable.value, variable.type) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Show more/less toggle if there are many variables -->
        <div v-if="allVariables.length > 6" class="show-more-section">
          <button 
            @click="showAllVariables = !showAllVariables"
            class="show-more-btn"
          >
            {{ showAllVariables ? 'Show Less' : `Show ${allVariables.length - 6} More` }}
            <svg 
              class="show-more-icon" 
              :class="{ 'rotated': showAllVariables }"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- No AGS Variables Selected Message -->
      <div v-else-if="!isSingleVariableMode && !isMultiVariableMode" class="no-filters-section">
        <div class="no-filters-message">
          <svg class="info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p>Select AGS variables from the sidebar to view data for this area.</p>
          <div class="filter-hint">
            <small>Use the AGS Data section to select specific variables to analyze.</small>
          </div>
        </div>
      </div>

      <!-- No AGS Data Available Message -->
      <div v-else-if="(isSingleVariableMode || isMultiVariableMode) && !hasAgsData" class="no-data-section">
        <div class="no-data-message">
          <svg class="no-data-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <p>No AGS data available for the selected variables in this area.</p>
        </div>
      </div>

      <!-- Debug Information (Development Only) -->
      <div v-if="isDevelopment && hasAgsData" class="debug-section">
        <h4>🔍 AGS Data Debug Information</h4>
        
        <!-- Step 1: Selected Variables & Raw Values -->
        <div class="debug-subsection">
          <h5>Step 1: Selected Variables & Extracted Values</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">Total Selected Variables:</span>
              <span class="value">{{ selectedAgsVariables.length }}</span>
            </div>
            <div v-for="variable in displayedVariables" :key="variable.path" class="debug-item">
              <span class="label">{{ variable.label }}:</span>
              <span class="value highlight-value">{{ variable.value !== null && variable.value !== undefined ? variable.value : 'N/A' }}</span>
            </div>
          </div>
        </div>

        <!-- Step 2: Viewport/State Comparison Range -->
        <div v-if="agsIntensityLevel && agsIntensityLevel !== 'no_data'" class="debug-subsection">
          <h5>Step 2: AGS Data Range (Backend Calculated)</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">Min AGS Index ({{ getUnitTypeLabel(unitType) }}s):</span>
              <span class="value">{{ getSimulatedAgsRange().min.toFixed(1) }}</span>
            </div>
            <div class="debug-item">
              <span class="label">Max AGS Index ({{ getUnitTypeLabel(unitType) }}s):</span>
              <span class="value">{{ getSimulatedAgsRange().max.toFixed(1) }}</span>
            </div>
            <div class="debug-item">
              <span class="label">Mean AGS Index:</span>
              <span class="value">{{ getSimulatedAgsRange().mean.toFixed(1) }}</span>
            </div>
            <div class="debug-item">
              <span class="label">Median AGS Index:</span>
              <span class="value">{{ getSimulatedAgsRange().median.toFixed(1) }}</span>
            </div>
            <div class="debug-item">
              <span class="label">Range Size:</span>
              <span class="value">{{ (getSimulatedAgsRange().max - getSimulatedAgsRange().min).toFixed(1) }}</span>
            </div>
            <div class="debug-item">
              <span class="label">Total {{ getUnitTypeLabel(unitType) }}s:</span>
              <span class="value">{{ getSimulatedAgsRange().totalEntities }}</span>
            </div>
          </div>
        </div>

        <!-- Step 3: Percentage Calculation (for 2+ variables) -->
        <div v-if="isMultiVariableMode && effectiveAgsLevel && effectiveAgsLevel !== 'no_data'" class="debug-subsection">
          <h5>Step {{ debugSteps.normalization }}: Multi-Variable Aggregation & Normalization</h5>
          <div class="debug-info">
            
            <!-- DETAILED CALCULATION (when backend provides breakdown) -->
            <template v-if="multiVariableDebugInfo.hasDetailedCalculation">
              <!-- Step a: Raw Variable Values -->
              <div class="debug-item calculation-step">
                <span class="label">Step a: Raw Variable Values</span>
                <span class="value formula">Individual values from AGS data</span>
              </div>
              
              <div v-for="variable in multiVariableDebugInfo.variables" :key="variable.path" class="debug-item calculation-step">
                <span class="label">{{ variable.label }}:</span>
                <span class="value highlight-value">{{ formatAgsVariableValue(variable.raw_value, 'number') }}</span>
              </div>
              
              <!-- Step b: Normalize Each Variable -->
              <div class="debug-item calculation-step">
                <span class="label">Step b: Normalize Each Variable</span>
                <span class="value formula">Convert each to 0-100 scale based on comparison range</span>
              </div>
              
              <div v-for="variable in multiVariableDebugInfo.variables" :key="'norm-' + variable.path" class="debug-item calculation-step">
                <span class="label">{{ variable.label }}:</span>
                <span class="value formula">
                  {{ formatAgsVariableValue(variable.raw_value, 'number') }} = {{ variable.normalized_value }}/100
                  <span v-if="variable.comparison_range" class="formula-detail">
                    (range: {{ variable.comparison_range.min }}-{{ variable.comparison_range.max }})
                  </span>
                </span>
              </div>
              
              <!-- Step c: Apply Scale Direction (Per Variable) -->
              <div class="debug-item calculation-step">
                <span class="label">Step c: Apply Scale Direction</span>
                <span class="value formula">If "higher is worse", invert: (1 - normalized/100)</span>
              </div>
              
              <div v-for="variable in multiVariableDebugInfo.variables" :key="'scale-' + variable.path" class="debug-item calculation-step">
                <span class="label">{{ variable.label }}:</span>
                <span class="value formula">
                  <span v-if="variable.scale_reversed">
                    {{ variable.normalized_value }}/100 = ((1 - {{ (variable.normalized_value / 100).toFixed(2) }})) = {{ variable.adjusted_value.toFixed(2) }}
                  </span>
                  <span v-else>
                    {{ variable.normalized_value }}/100 = {{ (variable.normalized_value / 100).toFixed(2) }} (no reversal)
                  </span>
                </span>
              </div>
              
              <!-- Step d: Apply Weights -->
              <div class="debug-item calculation-step">
                <span class="label">Step d: Apply Weights</span>
                <span class="value formula">Multiply adjusted value by weight</span>
              </div>
              
              <div v-for="variable in multiVariableDebugInfo.variables" :key="'weight-' + variable.path" class="debug-item calculation-step">
                <span class="label">{{ variable.label }}:</span>
                <span class="value formula">
                  {{ variable.adjusted_value.toFixed(2) }} × {{ variable.weight.toFixed(1) }} = {{ variable.weighted_contribution.toFixed(2) }}
                </span>
              </div>
              
              <!-- Step e: Calculate Weighted Sum -->
              <div class="debug-item calculation-step">
                <span class="label">Step e: Calculate Weighted Sum</span>
                <span class="value formula">Sum all weighted contributions</span>
              </div>
              
              <div class="debug-item highlight">
                <span class="label">✓ Aggregated Value:</span>
                <span class="value highlight-value">
                  {{ multiVariableDebugInfo.variables.map(v => v.weighted_contribution.toFixed(2)).join(' + ') }} = {{ multiVariableDebugInfo.aggregatedValue.toFixed(2) }}
                </span>
              </div>
              
              <!-- Step f: Convert to Percentage -->
              <div class="debug-item calculation-step">
                <span class="label">Step f: Convert to Percentage</span>
                <span class="value formula">Multiply by 100</span>
              </div>
              
              <!-- Final Result -->
              <div class="debug-item highlight">
                <span class="label">✓ Final Intensity Percentage:</span>
                <span class="value">{{ multiVariableDebugInfo.finalPercentage }}%</span>
              </div>
            </template>
            
            <!-- SIMPLIFIED VIEW (fallback when detailed calculation not available) -->
            <template v-else>
              <div class="debug-item">
                <span class="label">📊 Process:</span>
                <span class="value formula">Backend aggregates {{ multiVariableDebugInfo.variableCount }} variables → Normalizes to percentage</span>
              </div>
              
              <!-- Show individual variable values -->
              <div class="debug-item calculation-step">
                <span class="label">Step a: Raw Variable Values</span>
                <span class="value formula">Individual values from AGS data</span>
              </div>
              
              <div v-for="variable in multiVariableDebugInfo.variables" :key="variable.path" class="debug-item calculation-step">
                <span class="label">{{ variable.label }}:</span>
                <span class="value highlight-value">{{ formatAgsVariableValue(variable.raw_value, 'number') }}</span>
              </div>
              
              <!-- Backend aggregation -->
              <div class="debug-item calculation-step">
                <span class="label">Step b: Backend Aggregation</span>
                <span class="value formula">Backend normalizes each variable, applies weights, and aggregates into a single index</span>
              </div>
              
              <!-- Result -->
              <div class="debug-item highlight">
                <span class="label">✓ Final Intensity Percentage:</span>
                <span class="value">{{ multiVariableDebugInfo.finalPercentage }}%</span>
              </div>
            </template>
            
            <!-- Interpretation -->
            <div class="debug-item interpretation">
              <span class="label">📖 What This Means:</span>
              <span class="value formula">
                {{ multiVariableDebugInfo.explanation }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- Step 4: Single-Variable Normalization (for 1 variable) -->
        <div v-if="isSingleVariableMode && effectiveAgsLevel && effectiveAgsLevel !== 'no_data'" class="debug-subsection">
          <h5>Step {{ debugSteps.normalization }}: AGS Intensity Normalization</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">📊 Normalization Formula:</span>
              <span class="value formula">{{ singleVariableDebugInfo.formula }}</span>
            </div>
            
            <div class="debug-item">
              <span class="label">This Area's AGS Index:</span>
              <span class="value formula highlight-value">{{ singleVariableDebugInfo.index.toFixed(1) }}</span>
            </div>
            
            <!-- Calculation Steps -->
            <div class="debug-item calculation-step">
              <span class="label">Step a: Subtract Min</span>
              <span class="value formula">{{ singleVariableDebugInfo.steps.subtractMin.calculation }} = {{ singleVariableDebugInfo.steps.subtractMin.result }}</span>
            </div>
            
            <div class="debug-item calculation-step">
              <span class="label">Step b: Calculate Range</span>
              <span class="value formula">{{ singleVariableDebugInfo.steps.calculateRange.calculation }} = {{ singleVariableDebugInfo.steps.calculateRange.result }}</span>
            </div>
            
            <div class="debug-item calculation-step">
              <span class="label">Step c: Divide and Multiply</span>
              <span class="value formula">{{ singleVariableDebugInfo.steps.divideAndMultiply.calculation }} = {{ singleVariableDebugInfo.steps.divideAndMultiply.result }}%</span>
            </div>
            
            <!-- Result -->
            <div class="debug-item highlight">
              <span class="label">✓ Normalized Score:</span>
              <span class="value">{{ singleVariableDebugInfo.steps.divideAndMultiply.result }}%</span>
            </div>
            
            <!-- Interpretation -->
            <div class="debug-item interpretation">
              <span class="label">📖 What This Means:</span>
              <span class="value formula">
                {{ singleVariableDebugInfo.explanation }}
              </span>
            </div>
            
            <!-- Additional Context -->
            <div class="debug-item interpretation">
              <span class="label">🎯 Position:</span>
              <span class="value formula">
                {{ singleVariableDebugInfo.position }}
              </span>
            </div>
          </div>
        </div>

        <!-- Step 5: Intensity Level Classification -->
        <div v-if="effectiveAgsLevel && effectiveAgsLevel !== 'no_data'" class="debug-subsection">
          <h5>Step {{ debugSteps.classification }}: Map to Intensity Level & Color</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">Intensity Thresholds:</span>
              <span class="value formula">0-14% VeryLow, 14-28% Low, 28-42% LowMod, 42-57% Mod, 57-71% ModHigh, 71-85% High, 85-100% VeryHigh</span>
            </div>
            <div class="debug-item highlight">
              <span class="label">Calculated Percentage:</span>
              <span class="value">{{ displayedIntensityPercent }}%</span>
            </div>
            <div class="debug-item highlight">
              <span class="label">✓ Intensity Level:</span>
              <span class="value">{{ formatAgsIntensityLevel(effectiveAgsLevel) }}</span>
            </div>
            <div class="debug-item highlight">
              <span class="label">✓ Color Assignment:</span>
              <span class="value color-swatch" :style="{ backgroundColor: getAgsIntensityColor(effectiveAgsLevel), color: 'white' }">
                {{ getColorName(getAgsIntensityColor(effectiveAgsLevel)) }} ({{ getAgsIntensityColor(effectiveAgsLevel) }})
              </span>
            </div>
          </div>
        </div>

        <!-- Summary -->
        <div class="debug-summary">
          <strong>Summary:</strong> 
          <span v-if="isSingleVariableMode">
            Using <strong>1 variable</strong> with value <strong>{{ getSimulatedAgsIndex().toFixed(1) }}</strong>
            → Normalized to <strong>{{ singleVariablePercentage }}%</strong>
          </span>
          <span v-else>
            Applied multi-variable calculation to <strong>{{ selectedAgsVariables.length }} variables</strong>
            → Final percentage <strong>{{ multiVariablePercentage }}%</strong>
          </span>
          → Classified as <strong>{{ formatAgsIntensityLevel(effectiveAgsLevel) }}</strong>
          → Colored <strong :style="{ color: getAgsIntensityColor(effectiveAgsLevel) }">{{ getColorName(getAgsIntensityColor(effectiveAgsLevel)) }}</strong>
        </div>
      </div>

      <!-- Information Footer -->
      <div class="info-section">
        <p class="data-note">
          <small>
            <span v-if="hasAgsData">
              AGS data represents demographic, economic, and social characteristics for this geographic area. 
              Values are sourced from the American Geographic Survey and aggregated at the {{ getUnitTypeLabel(unitType).toLowerCase() }} level.
            </span>
            <span v-else>
              Click on geographic areas to view detailed AGS information and statistics.
            </span>
          </small>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { useAGSStore } from '../../../composables/useAGSStore';

export default {
  name: 'AGSDataPopup',
  props: {
    unitCode: {
      type: String,
      required: true
    },
    unitType: {
      type: String,
      required: true
    },
    stateCode: {
      type: String,
      default: null
    },
    countyCode: {
      type: String,
      default: null
    },
    tractCode: {
      type: String,
      default: null
    },
    blockGroupCode: {
      type: String,
      default: null
    },
    agsData: {
      type: Object,
      default: () => ({})
    },
    selectedAgsVariables: {
      type: Array,
      default: () => []
    },
    agsIntensityLevel: {
      type: String,
      default: null
    },
    agsRiskLevel: {
      type: String,
      default: null
    },
    dataYear: {
      type: [String, Number],
      default: 2025
    },
    agsValue: {
      type: Number,
      default: null
    },
    agsPercentage: {
      type: Number,
      default: null
    },
    agsComparisonReference: {
      type: Object,
      default: null
    },
    agsCalculationDetails: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showAllVariables: false,
      isMobile: false,
      agsStore: useAGSStore()
    };
  },
  computed: {
    // Access AGS scale reversal state from store
    agsScaleReversed() {
      return this.agsStore.agsScaleReversed;
    },
    
    // Use risk level if available (from state/county comparison), otherwise use intensity level (viewport)
    effectiveAgsLevel() {
      return this.agsRiskLevel || this.agsIntensityLevel;
    },
    
    // === MODE DETECTION ===
    isMultiVariableMode() {
      return this.selectedAgsVariables.length > 1;
    },
    
    isSingleVariableMode() {
      return this.selectedAgsVariables.length === 1;
    },
    
    // === PERCENTAGE CALCULATION ===
    displayedIntensityPercent() {
      return this.isMultiVariableMode 
        ? this.multiVariablePercentage 
        : this.singleVariablePercentage;
    },
    
    multiVariablePercentage() {
      if (this.agsPercentage !== null && this.agsPercentage !== undefined) {
        return Math.round(this.agsPercentage);
      }
      return this.fallbackPercentage;
    },
    
    singleVariablePercentage() {
      if (this.agsValue !== null && this.agsValue !== undefined && this.agsComparisonReference) {
        const min = this.agsComparisonReference.min_value || 0;
        const max = this.agsComparisonReference.max_value || 1;
        
        if (max === min) {
          return 50; // Default to middle if no range
        }
        
        // Calculate actual percentage: ((value - min) / (max - min)) × 100
        const percentage = ((this.agsValue - min) / (max - min)) * 100;
        const clamped = Math.max(0, Math.min(100, percentage)); // Clamp to 0-100
        return Math.round(clamped); // Round to nearest integer
      }
      return this.fallbackPercentage;
    },
    
    fallbackPercentage() {
      const intensityToPercent = {
        'very_low': 7,       // Middle of 0-14% range
        'low': 21,           // Middle of 14-28% range
        'low_moderate': 35,  // Middle of 28-42% range
        'moderate': 49,      // Middle of 42-57% range
        'moderate_high': 64, // Middle of 57-71% range
        'high': 78,          // Middle of 71-85% range
        'very_high': 92,     // Middle of 85-100% range
        'no_data': 0
      };
      return intensityToPercent[this.agsIntensityLevel] || 0;
    },
    
    // === DEBUG INFORMATION ===
    debugSteps() {
      return {
        normalization: 3,
        classification: 4
      };
    },
    
    multiVariableDebugInfo() {
      if (!this.isMultiVariableMode) return null;
      
      // Check if we have detailed calculation breakdown from backend
      const hasCalculationDetails = this.agsCalculationDetails && 
                                     this.agsCalculationDetails.variables && 
                                     this.agsCalculationDetails.variables.length > 0;
      
      if (hasCalculationDetails) {
        // Use detailed backend calculation data
        return {
          finalPercentage: this.displayedIntensityPercent,
          variableCount: this.selectedAgsVariables.length,
          variables: this.agsCalculationDetails.variables,
          aggregatedPercentage: this.agsCalculationDetails.aggregated_percentage,
          scaleReversed: this.agsCalculationDetails.scale_reversed || false,
          scaleAdjustmentApplied: this.agsCalculationDetails.scale_adjustment_applied || false,
          totalWeight: this.agsCalculationDetails.total_weight || 1.0,
          hasDetailedCalculation: true,
          explanation: `Backend normalized ${this.agsCalculationDetails.variables.length} variables, applied weights, and aggregated to ${this.agsCalculationDetails.aggregated_percentage}%, then ${this.agsCalculationDetails.scale_reversed ? 'reversed scale to' : 'finalized at'} ${this.displayedIntensityPercent}%.`
        };
      }
      
      // Fallback: Use basic variable data if detailed calculation not available
      const variableDetails = this.displayedVariables
        .filter(v => v.value !== null && v.value !== undefined)
        .map(v => ({
          label: v.label,
          raw_value: v.value,
          path: v.path
        }));
      
      return {
        finalPercentage: this.displayedIntensityPercent,
        variableCount: this.selectedAgsVariables.length,
        variables: variableDetails,
        hasDetailedCalculation: false,
        explanation: `Backend aggregated ${variableDetails.length} variables using weighted averaging, then normalized the result to ${this.displayedIntensityPercent}% based on comparison to similar ${this.getUnitTypeLabel(this.unitType).toLowerCase()}s.`
      };
    },
    
    singleVariableDebugInfo() {
      if (!this.isSingleVariableMode) return null;
      
      const range = this.getSimulatedAgsRange();
      const index = this.getSimulatedAgsIndex();
      
      return {
        formula: '((Index - Min) / (Max - Min)) × 100',
        index: index,
        range: range,
        steps: {
          subtractMin: {
            calculation: `${index.toFixed(1)} - ${range.min.toFixed(1)}`,
            result: (index - range.min).toFixed(1)
          },
          calculateRange: {
            calculation: `${range.max.toFixed(1)} - ${range.min.toFixed(1)}`,
            result: (range.max - range.min).toFixed(1)
          },
          divideAndMultiply: {
            calculation: `(${(index - range.min).toFixed(1)} / ${(range.max - range.min).toFixed(1)}) × 100`,
            result: this.getCalculatedAgsPercentage().toFixed(1)
          }
        },
        explanation: `This area's AGS index is ${this.getCalculatedAgsPercentage().toFixed(1)}% of the way from the lowest (${range.min.toFixed(1)}) to the highest (${range.max.toFixed(1)}) AGS index in similar ${this.getUnitTypeLabel(this.unitType).toLowerCase()}s.`,
        position: this.getAgsIndexPositionDescription()
      };
    },
    
    shouldShowPopup() {
      // Only show popup if we have geographic information AND at least one AGS variable is selected
      return this.unitCode && this.unitType && this.selectedAgsVariables.length > 0;
    },
    
    displayName() {
      const unitTypeNames = {
        'states': 'State',
        'zips': 'ZIP Code',
        'tracts': 'Census Tract',
        'block_groups': 'Block Group',
        'block_group': 'Block Group',
        'blocks': 'Block',
        'block': 'Block'
      };
      
      const typeName = unitTypeNames[this.unitType] || this.unitType;
      return `${typeName}: ${this.unitCode}`;
    },
    
    allVariables() {
      if (!this.agsData || !Object.keys(this.agsData).length || !this.selectedAgsVariables.length) return [];
      
      const variables = [];
      
      // Extract variables from AGS data based on selected variables
      for (const variable of this.selectedAgsVariables) {
        const categoryData = this.agsData[variable.category_name];
        if (categoryData && categoryData.extracted_values) {
          const value = categoryData.extracted_values[variable.path];
          if (value !== null && value !== undefined) {
            variables.push({
              ...variable,
              value: value,
              type: this.getVariableType(variable.path)
            });
          }
        }
      }
      
      return variables;
    },
    
    hasAgsData() {
      return this.agsData && Object.keys(this.agsData).length > 0 && this.allVariables.length > 0;
    },
    
    selectedCategoryDisplayName() {
      if (!this.hasAgsData) return null;
      
      const firstCategory = Object.values(this.agsData)[0];
      return firstCategory?.category_display_name || null;
    },
    
    displayedVariables() {
      if (this.showAllVariables) {
        return this.allVariables;
      }
      return this.allVariables.slice(0, 6);
    },

    isDevelopment() {
      return import.meta.env.DEV;
    },

    /**
     * Calculate ranking position for current area
     * Returns null if data is unavailable
     */
    agsRanking() {
      // Check if we have the required data
      if (!this.agsValue && this.agsValue !== 0) return null;
      if (!this.agsComparisonReference) return null;
      
      const { min_value, max_value, total_entities, scope, scope_name } = this.agsComparisonReference;
      
      // Validate comparison reference data
      if (min_value === null || min_value === undefined) return null;
      if (max_value === null || max_value === undefined) return null;
      if (!total_entities || total_entities <= 0) return null;
      
      const range = max_value - min_value;
      
      // If there's no range, we can't calculate a meaningful ranking
      if (range === 0) return null;
      
      // Calculate percentile position: ((agsValue - min) / (max - min)) × 100
      const percentile = ((this.agsValue - min_value) / range) * 100;
      
      // Clamp percentile to 0-100 range
      const clampedPercentile = Math.max(0, Math.min(100, percentile));
      
      // Convert percentile to rank: round((percentile / 100) × total_entities)
      // Higher AGS value = higher rank (higher position number)
      let rank = Math.round((clampedPercentile / 100) * total_entities);
      
      // Ensure rank is at least 1 and at most total_entities
      rank = Math.max(1, Math.min(total_entities, rank));
      
      return {
        rank: rank,
        total: total_entities,
        percentile: clampedPercentile,
        scope: scope || 'viewport',
        scopeName: scope_name || 'viewport'
      };
    },

    /**
     * Check if ranking data is available and should be displayed
     */
    hasRankingData() {
      return this.agsRanking !== null;
    }
  },
  
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    
    // Debug logging for props
    console.log('🎯 AgsDataPopup mounted with props:', {
      unitCode: this.unitCode,
      unitType: this.unitType,
      agsValue: this.agsValue,
      agsIntensityLevel: this.agsIntensityLevel,
      agsRiskLevel: this.agsRiskLevel,
      selectedAgsVariablesCount: this.selectedAgsVariables.length,
      selectedAgsVariables: this.selectedAgsVariables.map(v => v.label),
      hasAgsData: this.hasAgsData,
      agsComparisonReference: this.agsComparisonReference
    });
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
  },
  
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    
    getStateName(stateCode) {
      const stateNames = {
        '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas', '06': 'California',
        '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware', '11': 'District of Columbia',
        '12': 'Florida', '13': 'Georgia', '15': 'Hawaii', '16': 'Idaho', '17': 'Illinois',
        '18': 'Indiana', '19': 'Iowa', '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana',
        '23': 'Maine', '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota',
        '28': 'Mississippi', '29': 'Missouri', '30': 'Montana', '31': 'Nebraska', '32': 'Nevada',
        '33': 'New Hampshire', '34': 'New Jersey', '35': 'New Mexico', '36': 'New York',
        '37': 'North Carolina', '38': 'North Dakota', '39': 'Ohio', '40': 'Oklahoma',
        '41': 'Oregon', '42': 'Pennsylvania', '44': 'Rhode Island', '45': 'South Carolina',
        '46': 'South Dakota', '47': 'Tennessee', '48': 'Texas', '49': 'Utah', '50': 'Vermont',
        '51': 'Virginia', '53': 'Washington', '54': 'West Virginia', '55': 'Wisconsin', '56': 'Wyoming'
      };
      return stateNames[stateCode] || stateCode;
    },
    
    getCountyName(countyCode) {
      // This would typically come from a county lookup service
      return null;
    },
    
    formatTractCode(tractCode) {
      if (!tractCode) return tractCode;
      // Format as XXXX.XX if it's a 6-digit tract code
      if (tractCode.length === 6) {
        return `${tractCode.slice(0, 4)}.${tractCode.slice(4)}`;
      }
      return tractCode;
    },
    
    formatBlockGroupCode(blockGroupCode) {
      return blockGroupCode;
    },
    
    formatAgsIntensityLevel(level) {
      const levelNames = {
        'very_low': 'Very Low',
        'low': 'Low',
        'low_moderate': 'Low-Moderate',
        'moderate': 'Moderate',
        'moderate_high': 'Moderate-High',
        'high': 'High',
        'very_high': 'Very High',
        'no_data': 'No Data'
      };
      return levelNames[level] || level;
    },
    
    getAgsBadgeClass(level) {
      const classes = {
        'very_low': 'ags-badge-very-low',
        'low': 'ags-badge-low',
        'low_moderate': 'ags-badge-low-moderate',
        'moderate': 'ags-badge-moderate',
        'moderate_high': 'ags-badge-moderate-high',
        'high': 'ags-badge-high',
        'very_high': 'ags-badge-very-high',
        'no_data': 'ags-badge-no-data'
      };
      return classes[level] || 'ags-badge-no-data';
    },
    
    extractVariableValue(rawData, path) {
      // Handle temporal data structure (2025, 2030, etc.)
      const yearData = rawData[this.dataYear] || rawData['2025'] || rawData;
      
      if (!yearData) return null;
      
      // Handle path like "properties.HHDCY" -> extract HHDCY
      const pathParts = path.split('.');
      const variableName = pathParts[pathParts.length - 1];
      
      return yearData[variableName] || null;
    },
    
    getVariableType(path) {
      // All AGS variables are counts/numbers - no special formatting needed
      // The variable labels already include context (e.g., "Income Less than $10000")
      // so we don't need to format the values as currency, percentages, etc.
      return 'number';
    },
    
    formatAgsVariableValue(value, type) {
      if (value === null || value === undefined) return 'N/A';
      
      switch (type) {
        case 'currency':
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value);
        
        case 'percentage':
          return `${parseFloat(value).toFixed(1)}%`;
        
        case 'age':
          return `${parseFloat(value).toFixed(1)} years`;
        
        case 'number':
        default:
          return new Intl.NumberFormat('en-US').format(value);
      }
    },

    getUnitTypeLabel(unitType) {
      const labels = {
        'states': 'State',
        'counties': 'County',
        'zips': 'ZIP Code',
        'tracts': 'Census Tract',
        'block_group': 'Block Group',
        'block_groups': 'Block Group',
        'block': 'Block',
        'blocks': 'Block'
      };
      return labels[unitType] || 'Geographic Unit';
    },



    getDisplayedIntensityPercent() {
      return this.displayedIntensityPercent;
    },

    getAgsIntensityColor(level) {
      // Use centralized color logic from AGS store
      return this.agsStore.getAgsIntensityColor(level);
    },

    getColorName(hexColor) {
      const colorNames = {
        '#059669': this.agsScaleReversed ? 'Dark Green (Best)' : 'Dark Green (Lowest)',
        '#22c55e': this.agsScaleReversed ? 'Green (High)' : 'Green (Low)',
        '#84cc16': this.agsScaleReversed ? 'Yellow-Green (Moderate-High)' : 'Yellow-Green (Low-Moderate)',
        '#eab308': 'Yellow (Moderate)',
        '#f97316': this.agsScaleReversed ? 'Orange (Low-Moderate)' : 'Orange (Moderate-High)',
        '#ef4444': this.agsScaleReversed ? 'Red (Low)' : 'Red (High)',
        '#dc2626': this.agsScaleReversed ? 'Dark Red (Lowest)' : 'Dark Red (Highest)',
        '#9ca3af': 'Gray (No Data)'
      };
      return colorNames[hexColor] || hexColor;
    },

    /**
     * Get the percentage range description for an intensity level
     */
    getIntensityRangeDescription(intensityLevel) {
      const rangeDescriptions = {
        'very_low': '0-14% range',
        'low': '14-28% range',
        'low_moderate': '28-42% range',
        'moderate': '42-57% range',
        'moderate_high': '57-71% range',
        'high': '71-85% range',
        'very_high': '85-100% range',
        'no_data': '0% (no data)'
      };
      return rangeDescriptions[intensityLevel] || 'unknown range';
    },

    /**
     * Get detailed explanation of the intensity level
     */
    getIntensityLevelExplanation(intensityLevel) {
      const explanations = {
        'very_low': 'Significantly below average for similar areas',
        'low': 'Below average for similar areas',
        'low_moderate': 'Slightly below average for similar areas',
        'moderate': 'Average for similar areas',
        'moderate_high': 'Slightly above average for similar areas',
        'high': 'Above average for similar areas',
        'very_high': 'Significantly above average for similar areas',
        'no_data': 'No data available for comparison'
      };
      return explanations[intensityLevel] || 'Unknown intensity level';
    },

    /**
     * Get the calculation method description
     */
    getCalculationMethodDescription() {
      return `Backend processes ${this.selectedAgsVariables.length} AGS variables using a weighted aggregation algorithm, then compares the result to similar ${this.getUnitTypeLabel(this.unitType).toLowerCase()}s to determine relative intensity.`;
    },

    /**
     * Get actual AGS range from backend comparison reference data
     */
    getSimulatedAgsRange() {
      // Debug logging
      console.log('🔍 getSimulatedAgsRange called:', {
        hasComparisonReference: !!this.agsComparisonReference,
        comparisonReference: this.agsComparisonReference,
        agsValue: this.agsValue
      });
      
      // Use actual comparison reference data from backend if available
      if (this.agsComparisonReference) {
        console.log('✅ Using actual backend comparison reference');
        return {
          min: this.agsComparisonReference.min_value || 0,
          max: this.agsComparisonReference.max_value || 1,
          mean: this.agsComparisonReference.mean_value || 0,
          median: this.agsComparisonReference.median_value || 0,
          totalEntities: this.agsComparisonReference.total_entities || 0
        };
      }
      
      console.log('⚠️ Falling back to simulated range');
      
      // Fallback to simulated range if backend data not available
      const baseRanges = {
        'states': { min: 50, max: 200, mean: 125, median: 120 },
        'counties': { min: 30, max: 180, mean: 105, median: 100 },
        'zips': { min: 20, max: 160, mean: 90, median: 85 },
        'tracts': { min: 15, max: 150, mean: 82, median: 78 },
        'block_group': { min: 10, max: 140, mean: 75, median: 70 },
        'block': { min: 5, max: 130, mean: 67, median: 62 }
      };

      const range = baseRanges[this.unitType] || baseRanges['block_group'];
      
      // Add some variation based on unit code for realism
      const hash = this.simpleHash(this.unitCode);
      const variation = (hash % 20) - 10; // -10 to +10 variation
      
      return {
        min: range.min + variation,
        max: range.max + variation,
        mean: range.mean + variation,
        median: range.median + variation,
        totalEntities: this.getEstimatedEntityCount()
      };
    },

    /**
     * Get actual AGS index for this area from backend data
     */
    getSimulatedAgsIndex() {
      // Debug logging
      console.log('🔍 getSimulatedAgsIndex called:', {
        hasAgsValue: this.agsValue !== null && this.agsValue !== undefined,
        agsValue: this.agsValue,
        agsValueType: typeof this.agsValue,
        selectedVariablesCount: this.selectedAgsVariables.length,
        selectedVariables: this.selectedAgsVariables.map(v => ({ label: v.label, path: v.path })),
        allVariables: this.allVariables.map(v => ({ label: v.label, value: v.value }))
      });
      
      // Use actual AGS value from backend if available
      if (this.agsValue !== null && this.agsValue !== undefined) {
        console.log('✅ Using actual AGS value:', this.agsValue);
        console.log('📊 AGS Value Details:', {
          value: this.agsValue,
          comparisonReference: this.agsComparisonReference,
          intensityLevel: this.agsIntensityLevel,
          riskLevel: this.agsRiskLevel
        });
        return this.agsValue;
      }
      
      console.log('⚠️ Falling back to calculated value');
      
      // Fallback to calculated value based on intensity level
      const range = this.getSimulatedAgsRange();
      const percentage = this.getDisplayedIntensityPercent() / 100;
      
      // Calculate index based on percentage within range
      return range.min + (percentage * (range.max - range.min));
    },

    /**
     * Get calculated AGS percentage (reverse calculation for demonstration)
     */
    getCalculatedAgsPercentage() {
      const range = this.getSimulatedAgsRange();
      const index = this.getSimulatedAgsIndex();
      
      if (range.max === range.min) return 0;
      
      return ((index - range.min) / (range.max - range.min)) * 100;
    },

    /**
     * Get estimated entity count for the unit type
     */
    getEstimatedEntityCount() {
      const entityCounts = {
        'states': 50,
        'counties': 3143,
        'zips': 33144,
        'tracts': 85000,
        'block_group': 242000,
        'block': 11000000
      };
      
      return entityCounts[this.unitType] || 1000;
    },

    /**
     * Get AGS index position description
     */
    getAgsIndexPositionDescription() {
      const index = this.getSimulatedAgsIndex();
      const range = this.getSimulatedAgsRange();
      
      if (index < range.median) {
        const diff = ((range.median - index) / range.median * 100).toFixed(0);
        return `${diff}% below median (lower intensity than average)`;
      } else if (index > range.median) {
        const diff = ((index - range.median) / range.median * 100).toFixed(0);
        return `${diff}% above median (higher intensity than average)`;
      } else {
        return 'At median (average intensity level)';
      }
    },

    /**
     * Simple hash function for consistent pseudo-random values
     */
    simpleHash(str) {
      let hash = 0;
      if (!str || str.length === 0) return hash;
      
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      return Math.abs(hash);
    },

    /**
     * Format ordinal suffix for numbers (1st, 2nd, 3rd, etc.)
     * @param {number} num - The number to format
     * @returns {string} - Number with ordinal suffix
     */
    getOrdinalSuffix(num) {
      const j = num % 10;
      const k = num % 100;
      
      if (j === 1 && k !== 11) return num + 'st';
      if (j === 2 && k !== 12) return num + 'nd';
      if (j === 3 && k !== 13) return num + 'rd';
      return num + 'th';
    },

    /**
     * Get scope display text for ranking
     * @param {string} scope - The scope type ('viewport', 'state', 'county')
     * @param {string} scopeName - The name of the scope
     * @returns {string} - Formatted scope text
     */
    getScopeDisplayText(scope, scopeName) {
      if (scope === 'viewport') return 'in viewport';
      if (scope === 'state') return `in ${scopeName}`;
      if (scope === 'county') return `in ${scopeName}`;
      return '';
    },

    /**
     * Get percentile description for badge display
     * @param {number} percentile - The percentile value (0-100)
     * @returns {string} - Description text for the percentile
     */
    getPercentileDescription(percentile) {
      // Handle edge case: 100th percentile (highest value)
      if (percentile === 100) return 'Highest';
      // Handle edge case: 0th percentile (lowest value)
      if (percentile === 0) return 'Lowest';
      
      // Top quartile (75-99th percentile)
      if (percentile >= 75) return `Top ${Math.round(100 - percentile)}%`;
      // Bottom quartile (1-25th percentile)
      if (percentile <= 25) return `Bottom ${Math.round(percentile)}%`;
      // Middle range (26-74th percentile)
      return 'Middle range';
    },

    /**
     * Format complete ranking text
     * @param {object} ranking - Ranking object with rank, total, scope, scopeName
     * @returns {string} - Formatted ranking text
     */
    formatRanking(ranking) {
      const ordinal = this.getOrdinalSuffix(ranking.rank);
      const scopeText = this.getScopeDisplayText(ranking.scope, ranking.scopeName);
      return `${ordinal} out of ${ranking.total.toLocaleString()} ${scopeText}`;
    },

    /**
     * Get CSS class for percentile badge based on percentile value
     * @param {number} percentile - The percentile value (0-100)
     * @returns {string} - CSS class name
     */
    getPercentileBadgeClass(percentile) {
      if (percentile >= 75) return 'top';
      if (percentile <= 25) return 'bottom';
      return 'middle';
    }
  }
};
</script>

<style scoped>
/* Base popup styles */
.ags-popup {
  max-width: 380px;
  min-width: 300px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  line-height: 1.5;
}

.ags-popup.mobile {
  max-width: 280px;
  min-width: 250px;
}

/* Header Styles */
.popup-header {
  background: #f8f9fa;
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
}

.unit-name {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  line-height: 1.3;
  word-wrap: break-word;
}

/* Content Styles */
.popup-content {
  padding: 16px;
}

/* Sections */
.geographic-section,
.ags-intensity-section,
.ags-data-section,
.no-data-section,
.no-filters-section,
.info-section {
  margin-bottom: 16px;
}

.geographic-section:last-child,
.ags-intensity-section:last-child,
.ags-data-section:last-child,
.no-data-section:last-child,
.no-filters-section:last-child,
.info-section:last-child {
  margin-bottom: 0;
}

/* Section headers */
.geographic-section h4,
.ags-intensity-section h4,
.ags-data-section h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.section-header-with-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.section-header-with-info h4 {
  margin: 0;
}

/* Geographic info */
.geographic-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.geographic-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f9fafb;
  border-radius: 6px;
  font-size: 13px;
}

.label {
  color: #6b7280;
  font-weight: 500;
}

.value {
  color: #111827;
  font-weight: 600;
}

.code-value {
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
  background: #e5e7eb;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

/* AGS intensity info */
.ags-info-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ags-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 6px;
}

.ags-info-label {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

/* AGS badges */
.ags-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ags-badge-very-low { background: #059669; color: white; }
.ags-badge-low { background: #22c55e; color: white; }
.ags-badge-low-moderate { background: #84cc16; color: white; }
.ags-badge-moderate { background: #eab308; color: white; }
.ags-badge-moderate-high { background: #f97316; color: white; }
.ags-badge-high { background: #ef4444; color: white; }
.ags-badge-very-high { background: #dc2626; color: white; }
.ags-badge-no-data { background: #f3f4f6; color: #6b7280; }

/* AGS Table Styles */
.ags-table-container {
  overflow-x: auto;
  margin-bottom: 12px;
}

.ags-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
}

.ags-table th {
  background: #f8f9fa;
  padding: 8px 6px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  font-size: 12px;
}

.ags-table td {
  padding: 8px 6px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

.variable-type .variable-label {
  font-weight: 500;
  color: #374151;
}

.variable-value .value-number {
  font-weight: 500;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Intensity Summary Styles */
.intensity-summary {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  margin-top: 12px;
}

.intensity-bar-container {
  margin-bottom: 8px;
}

.intensity-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.intensity-bar {
  position: relative;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 4px;
}

.intensity-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
}

/* 7-level intensity bar colors matching map colors */
/* Ascending scale (default): Lower is better - green for low, red for high */
.intensity-fill.intensity-very-low {
  background: #059669; /* Dark green */
}

.intensity-fill.intensity-low {
  background: #22c55e; /* Green */
}

.intensity-fill.intensity-low-moderate {
  background: #84cc16; /* Yellow-green */
}

.intensity-fill.intensity-moderate {
  background: #eab308; /* Yellow */
}

.intensity-fill.intensity-moderate-high {
  background: #f97316; /* Orange */
}

.intensity-fill.intensity-high {
  background: #ef4444; /* Red */
}

.intensity-fill.intensity-very-high {
  background: #dc2626; /* Dark red */
}

/* Descending scale (reversed): Higher is better - red for low, green for high */
.intensity-fill.intensity-very-low.reversed {
  background: #dc2626; /* Dark red (worst) */
}

.intensity-fill.intensity-low.reversed {
  background: #ef4444; /* Red */
}

.intensity-fill.intensity-low-moderate.reversed {
  background: #f97316; /* Orange */
}

.intensity-fill.intensity-moderate.reversed {
  background: #eab308; /* Yellow (stays same) */
}

.intensity-fill.intensity-moderate-high.reversed {
  background: #84cc16; /* Yellow-green */
}

.intensity-fill.intensity-high.reversed {
  background: #22c55e; /* Green */
}

.intensity-fill.intensity-very-high.reversed {
  background: #059669; /* Dark green (best) */
}

.intensity-text {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
}

.combined-value {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.combined-value .label {
  color: #6c757d;
}

.combined-value .value {
  font-weight: 600;
  color: #212529;
}

/* Percentile Badge Styles */
.percentile-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.percentile-badge.top {
  background: #dcfce7;
  color: #166534;
}

.percentile-badge.bottom {
  background: #fee2e2;
  color: #991b1b;
}

.percentile-badge.middle {
  background: #fef3c7;
  color: #92400e;
}

/* Show more section */
.show-more-section {
  margin-top: 16px;
  text-align: center;
}

.show-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.show-more-btn:hover {
  background: #e2e8f0;
  border-color: #94a3b8;
}

.show-more-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.2s;
}

.show-more-icon.rotated {
  transform: rotate(180deg);
}

/* No Filters Selected Styles */
.no-filters-section {
  text-align: center;
  padding: 20px 0;
}

.no-filters-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #6b7280;
}

.info-icon {
  width: 28px;
  height: 28px;
  color: #3b82f6;
}

.no-filters-message p {
  margin: 0;
  font-size: 14px;
  text-align: center;
  color: #374151;
  font-weight: 500;
}

.filter-hint {
  background: #eff6ff;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 3px solid #3b82f6;
}

.filter-hint small {
  color: #1e40af;
  font-size: 12px;
}

/* No data section */
.no-data-section {
  text-align: center;
  padding: 20px 0;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #6b7280;
}

.no-data-icon {
  width: 24px;
  height: 24px;
  color: #9ca3af;
}

.no-data-message p {
  margin: 0;
  font-size: 14px;
  text-align: center;
}

/* Debug Section Styles */
.debug-section {
  background: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  max-height: 600px;
  overflow-y: auto;
}

.debug-section h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 700;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #fbbf24;
  padding-bottom: 8px;
}

.debug-subsection {
  background: white;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 10px;
  border-left: 3px solid #f59e0b;
}

.debug-subsection:last-of-type {
  margin-bottom: 0;
}

.debug-subsection h5 {
  margin: 0 0 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.debug-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.debug-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 11px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  padding: 4px 6px;
  border-radius: 3px;
  background: #fefce8;
}

.debug-item.highlight {
  background: #fef08a;
  border: 1px solid #facc15;
  font-weight: 600;
}

.debug-item .label {
  color: #92400e;
  font-weight: 500;
  min-width: 140px;
  flex-shrink: 0;
}

.debug-item .value {
  color: #451a03;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

.debug-item .value.formula {
  font-size: 10px;
  color: #78350f;
  font-style: italic;
}

.debug-item .value.formula .formula-detail {
  font-size: 9px;
  color: #a16207;
  margin-left: 4px;
}

.debug-item .value.highlight-value {
  background: #fef3c7;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 700;
  color: #92400e;
}

.debug-item.calculation-step {
  background: #fef9c3;
  border-left: 3px solid #facc15;
  padding-left: 10px;
}

.debug-item.interpretation {
  background: #dbeafe;
  border: 1px solid #60a5fa;
  padding: 8px;
  margin-top: 4px;
}

.debug-item.interpretation .label {
  color: #1e40af;
}

.debug-item.interpretation .value {
  color: #1e3a8a;
  font-size: 11px;
  line-height: 1.4;
}

.debug-item .value.color-swatch {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #d97706;
  min-width: 80px;
  text-align: center;
}

.debug-summary {
  background: #fffbeb;
  border: 2px solid #fbbf24;
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.6;
  color: #78350f;
}

.debug-summary strong {
  color: #92400e;
  font-weight: 700;
}

/* Label with Tooltip */
.label-with-tooltip {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Info Tooltip Styles */
.info-tooltip-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
}

.info-icon-small {
  width: 16px;
  height: 16px;
  color: #3b82f6;
  flex-shrink: 0;
}

.info-icon-tiny {
  width: 14px;
  height: 14px;
  color: #3b82f6;
  flex-shrink: 0;
}

.info-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 10px 12px;
  background: #1e293b;
  color: white;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  width: 240px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
}

.info-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #1e293b;
}

.info-tooltip-container:hover .info-tooltip {
  opacity: 1;
  visibility: visible;
}

.info-tooltip strong {
  display: block;
  margin-bottom: 4px;
  color: #60a5fa;
  font-size: 13px;
}

.info-tooltip p {
  margin: 0;
  color: #e2e8f0;
}

.info-tooltip p + p {
  margin-top: 6px;
}

/* Info Section Styles */
.info-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
}

.data-note {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
  line-height: 1.4;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .ags-popup {
    max-height: 70vh;
  }
  
  .popup-content {
    padding: 16px;
  }
  
  .ags-variables-grid {
    gap: 8px;
  }
  
  .ags-variable-item {
    padding: 10px;
  }
}
</style>